import { useTranslation } from 'react-i18next';
import trusstorLogo from '@assets/projectMerge/trusstor-logo.svg';
import { AlertIcon, RefreshIcon, SearchIcon } from '@utils/icons';
import type { ITrusstorProject } from '@interfaces/IProjectMerge';
import { Button } from '@components/ui/Button/Button';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import { SearchInput } from '@components/ui/SearchInput/SearchInput';
import { ConnectPrompt, SkeletonList, StatePanel } from './PaneState';
import type { PaneViewState } from './GenbaPane';
import type { ILinkedInfo } from './SiteRow';
import { TrusstorRow } from './SiteRow';
import pane from './pane.module.css';
import styles from './TrusstorPane.module.css';

interface ITrusstorPaneProps {
  viewState: PaneViewState;
  search: string;
  onSearchChange: (value: string) => void;
  rows: ITrusstorProject[];
  total: number;
  selected: string | null;
  onSelect: (id: string) => void;
  /** Trusstor id -> link partner (other side) for already-linked rows. */
  linkedMap: Record<string, ILinkedInfo>;
  onRetry: () => void;
  /** (Re)bind Trusstor — fired from the noCreds / reauth connect panels. */
  onConnect: () => void;
}

/** Right board pane — Trusstor projects (field reality). */
export function TrusstorPane({
  viewState,
  search,
  onSearchChange,
  rows,
  total,
  selected,
  onSelect,
  linkedMap,
  onRetry,
  onConnect,
}: ITrusstorPaneProps) {
  const { t } = useTranslation();
  const showTools = viewState === 'data' || viewState === 'empty';

  return (
    <section
      className={`${pane.pane} ${styles.trusstor}`}
      aria-label={t('projectMerge.trusstorPane.ariaLabel')}
    >
      <header className={pane.head}>
        <div className={pane.id}>
          <span className={`${pane.mark} ${styles.wordmark}`}>
            <img src={trusstorLogo} alt="" />
          </span>
          <span className={pane.idText}>
            <span className={pane.eyebrow}>{t('projectMerge.trusstorPane.eyebrow')}</span>
            <span className={pane.name}>{t('projectMerge.trusstorPane.name')}</span>
          </span>
          <span className={pane.count}>{t('projectMerge.trusstorPane.count', { count: total })}</span>
        </div>

        {showTools ? (
          <div className={pane.tools}>
            <SearchInput
              value={search}
              onChange={onSearchChange}
              placeholder={t('projectMerge.trusstorPane.searchPlaceholder')}
              ariaLabelClear={t('projectMerge.trusstorPane.clearSearch')}
            />
          </div>
        ) : null}
      </header>

      {viewState === 'loading' ? <SkeletonList rows={5} /> : null}

      {viewState === 'noCreds' ? (
        <ConnectPrompt
          titleKey="projectMerge.paneState.noCredsTrusstorTitle"
          bodyKey="projectMerge.paneState.noCredsTrusstorBody"
          ctaKey="projectMerge.paneState.connectTrusstor"
          onConnect={onConnect}
        />
      ) : null}

      {viewState === 'reauth' ? (
        <ConnectPrompt
          titleKey="projectMerge.paneState.reauthTrusstorTitle"
          bodyKey="projectMerge.paneState.reauthTrusstorBody"
          ctaKey="projectMerge.paneState.reconnectTrusstor"
          onConnect={onConnect}
        />
      ) : null}

      {viewState === 'error' ? (
        <StatePanel
          variant="error"
          icon={<AlertIcon size={24} />}
          title={t('projectMerge.paneState.errorTrusstorTitle')}
          actions={
            <Button variant="secondary" size="sm" onClick={onRetry}>
              <RefreshIcon size={14} />
              {t('projectMerge.paneState.retry')}
            </Button>
          }
        >
          {t('projectMerge.paneState.errorTrusstorBody')}
        </StatePanel>
      ) : null}

      {viewState === 'empty' ? (
        <StatePanel
          variant="neutral"
          icon={<SearchIcon size={22} />}
          title={t('projectMerge.paneState.emptyTrusstorTitle')}
        >
          {t('projectMerge.paneState.emptyTrusstorBody', { query: search })}
        </StatePanel>
      ) : null}

      {viewState === 'data' ? (
        <ScrollArea className={pane.list} viewportClassName={pane.listBody}>
          {rows.map((p) => (
            <TrusstorRow
              key={p.id}
              proj={p}
              selected={selected === p.id}
              linkedInfo={linkedMap[p.id]}
              onSelect={onSelect}
            />
          ))}
        </ScrollArea>
      ) : null}
    </section>
  );
}
